import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, AlertCircle, Loader2, RefreshCw, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const MAX_PIECES_PER_SLIP = 120;

const SFGCreatePickSlip = () => {
  const navigate = useNavigate();
  
  // Cascading filter states
  const [designNames, setDesignNames] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  
  const [selectedDesignName, setSelectedDesignName] = useState('');
  const [selectedShade, setSelectedShade] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [quantity, setQuantity] = useState(120);

  // 2026-05-18 — Multi-barcode picker. When the same combo has been
  // split across multiple customer-specific barcodes in rates_master,
  // we surface a selector. When only one barcode exists for the combo,
  // we hide the picker entirely (auto-select).
  const [itemBarcodeOptions, setItemBarcodeOptions] = useState([]);
  const [selectedItemBarcode, setSelectedItemBarcode] = useState('');
  const [loadingItemBarcodes, setLoadingItemBarcodes] = useState(false);
  
  // Availability check state
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Loading states
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [loadingShades, setLoadingShades] = useState(false);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  const [creating, setCreating] = useState(false);
  const [designSearchOpen, setDesignSearchOpen] = useState(false);

  // Fetch design names on mount
  useEffect(() => {
    fetchDesignNames();
  }, []);

  const fetchDesignNames = async () => {
    setLoadingDesigns(true);
    try {
      const response = await apiClient.get('/wms/sfg/pick-slips/dropdowns/design-names');
      setDesignNames(response.data.design_names || []);
    } catch (error) {
      console.error('Error fetching design names:', error);
      toast.error('Failed to load design names');
    } finally {
      setLoadingDesigns(false);
    }
  };

  const fetchShades = useCallback(async (designName) => {
    if (!designName) {
      setShades([]);
      return;
    }
    setLoadingShades(true);
    try {
      const response = await apiClient.get('/wms/sfg/pick-slips/dropdowns/shades', {
        params: { design_name: designName }
      });
      const shadeList = response.data.shades || [];
      setShades(shadeList);
      // Auto-fill if only one option
      if (shadeList.length === 1) {
        setSelectedShade(shadeList[0]);
        fetchSizes(designName, shadeList[0]);
      }
    } catch (error) {
      console.error('Error fetching shades:', error);
    } finally {
      setLoadingShades(false);
    }
  }, []);

  const fetchSizes = useCallback(async (designName, shade) => {
    if (!designName || !shade) {
      setSizes([]);
      return;
    }
    setLoadingSizes(true);
    try {
      const response = await apiClient.get('/wms/sfg/pick-slips/dropdowns/sizes', {
        params: { design_name: designName, shade: shade }
      });
      const sizeList = response.data.sizes || [];
      setSizes(sizeList);
      // Auto-fill if only one option
      if (sizeList.length === 1) {
        setSelectedSize(sizeList[0]);
        fetchDescriptions(designName, shade, sizeList[0]);
      }
    } catch (error) {
      console.error('Error fetching sizes:', error);
    } finally {
      setLoadingSizes(false);
    }
  }, []);

  const fetchDescriptions = useCallback(async (designName, shade, size) => {
    if (!designName || !shade || !size) {
      setDescriptions([]);
      return;
    }
    setLoadingDescriptions(true);
    try {
      const response = await apiClient.get('/wms/sfg/pick-slips/dropdowns/descriptions', {
        params: { design_name: designName, shade: shade, size: size }
      });
      const descList = response.data.descriptions || [];
      setDescriptions(descList);
      // Auto-fill if only one option
      if (descList.length === 1) {
        setSelectedDescription(descList[0]);
        // Defer to fetchItemBarcodes; reference via the ref to break the
        // declaration-order cycle (fetchItemBarcodes is declared below).
        fetchItemBarcodesRef.current?.(designName, shade, size, descList[0]);
      }
    } catch (error) {
      console.error('Error fetching descriptions:', error);
    } finally {
      setLoadingDescriptions(false);
    }
  }, []);

  const checkAvailability = useCallback(async (designName, shade, size, description, itemBarcode) => {
    if (!designName || !shade || !size || !description) {
      setAvailability(null);
      return;
    }
    setLoadingAvailability(true);
    try {
      const params = { design_name: designName, shade: shade, size: size, description: description };
      if (itemBarcode) params.item_barcode = itemBarcode;
      const response = await apiClient.get('/wms/sfg/pick-slips/check-availability', { params });
      setAvailability(response.data);
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailability(null);
    } finally {
      setLoadingAvailability(false);
    }
  }, []);

  const fetchItemBarcodes = useCallback(async (designName, shade, size, description) => {
    if (!designName || !shade || !size || !description) {
      setItemBarcodeOptions([]);
      setSelectedItemBarcode('');
      return;
    }
    setLoadingItemBarcodes(true);
    try {
      const response = await apiClient.get('/wms/sfg/pick-slips/dropdowns/item-barcodes', {
        params: { design_name: designName, shade: shade, size: size, description: description },
      });
      const options = response.data.options || [];
      setItemBarcodeOptions(options);
      // 2026-05-18 (P0 user fix): always surface the picker when at least
      // one rates_master barcode exists — operator wants to SEE the
      // barcode + MRP they're picking, not have it auto-selected silently.
      // Only auto-skip the picker when zero options come back (no
      // rates_master entry at all) — that case falls through to legacy
      // availability lookup.
      if (options.length === 0) {
        setSelectedItemBarcode('');
        checkAvailability(designName, shade, size, description);
      } else {
        // ≥1 barcode option → operator must pick first; clear stale availability.
        setSelectedItemBarcode('');
        setAvailability(null);
      }
    } catch (error) {
      console.error('Error fetching item barcodes:', error);
      setItemBarcodeOptions([]);
      setSelectedItemBarcode('');
      // Fall back to legacy single-lookup behaviour.
      checkAvailability(designName, shade, size, description);
    } finally {
      setLoadingItemBarcodes(false);
    }
  }, [checkAvailability]);

  // Keep a ref to the latest fetchItemBarcodes so fetchDescriptions can
  // call it without participating in the dep cycle.
  const fetchItemBarcodesRef = useRef(fetchItemBarcodes);
  useEffect(() => {
    fetchItemBarcodesRef.current = fetchItemBarcodes;
  }, [fetchItemBarcodes]);

  // Handle cascading changes
  const handleDesignNameChange = (value) => {
    setSelectedDesignName(value);
    setSelectedShade('');
    setSelectedSize('');
    setSelectedDescription('');
    setAvailability(null);
    setShades([]);
    setSizes([]);
    setDescriptions([]);
    setItemBarcodeOptions([]);
    setSelectedItemBarcode('');
    fetchShades(value);
  };

  const handleShadeChange = (value) => {
    setSelectedShade(value);
    setSelectedSize('');
    setSelectedDescription('');
    setAvailability(null);
    setSizes([]);
    setDescriptions([]);
    setItemBarcodeOptions([]);
    setSelectedItemBarcode('');
    fetchSizes(selectedDesignName, value);
  };

  const handleSizeChange = (value) => {
    setSelectedSize(value);
    setSelectedDescription('');
    setAvailability(null);
    setDescriptions([]);
    setItemBarcodeOptions([]);
    setSelectedItemBarcode('');
    fetchDescriptions(selectedDesignName, selectedShade, value);
  };

  const handleDescriptionChange = (value) => {
    setSelectedDescription(value);
    setSelectedItemBarcode('');
    setAvailability(null);
    fetchItemBarcodes(selectedDesignName, selectedShade, selectedSize, value);
  };

  const handleItemBarcodeChange = (value) => {
    setSelectedItemBarcode(value);
    checkAvailability(selectedDesignName, selectedShade, selectedSize, selectedDescription, value);
  };

  const handleCreateSlip = async () => {
    if (!selectedDesignName || !selectedShade || !selectedSize || !selectedDescription) {
      toast.error('Please select all fields');
      return;
    }

    if (quantity <= 0) {
      toast.error('Enter a quantity greater than 0');
      return;
    }

    if (!availability || availability.available_pieces === 0) {
      toast.error('No stock available for selected product');
      return;
    }

    setCreating(true);
    try {
      const response = await apiClient.post('/wms/sfg/pick-slips/create-manual', {
        design_name: selectedDesignName,
        shade: selectedShade,
        size: selectedSize,
        description: selectedDescription,
        quantity: quantity,
        item_barcode: selectedItemBarcode || undefined,
      });

      if (response.data.success) {
        const { slips = [], slips_created = 1, total_allocated = 0, shortfall = 0 } = response.data;
        const slipNos = slips.map((s) => s.slip_no).join(', ');
        toast.success(response.data.message || 'Pick slip(s) created!', {
          description: slips_created > 1
            ? `${slips_created} slips · ${total_allocated} pcs (120/slip): ${slipNos}${shortfall > 0 ? ` · ${shortfall} pcs SHORT` : ''}`
            : `${total_allocated} pieces${shortfall > 0 ? ` · ${shortfall} pcs SHORT` : ''}`,
          duration: shortfall > 0 ? 10000 : 6000,
        });

        // Reset form
        setSelectedDesignName('');
        setSelectedShade('');
        setSelectedSize('');
        setSelectedDescription('');
        setQuantity(120);
        setAvailability(null);
        setShades([]);
        setSizes([]);
        setDescriptions([]);
        setItemBarcodeOptions([]);
        setSelectedItemBarcode('');

        // Refresh design names (some may no longer have stock)
        fetchDesignNames();
      } else {
        toast.error(response.data.message || 'Failed to create pick slip');
      }
    } catch (error) {
      console.error('Error creating pick slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pick slip');
    } finally {
      setCreating(false);
    }
  };

  // When the rates revision has split the same combo across multiple
  // customer-specific barcodes, the operator must pick one before we
  // allow create. With a single option we auto-select so this check
  // passes silently.
  const needsBarcodeChoice = itemBarcodeOptions.length >= 1 && !selectedItemBarcode;
  const canCreate = selectedDesignName && selectedShade && selectedSize && selectedDescription && 
                    !needsBarcodeChoice &&
                    quantity > 0 &&
                    availability?.available_pieces > 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/wms/sfg')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create SFG Pick Slip</h1>
          <p className="text-gray-500">Select product and quantity to create a pick slip</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Cascading Filters Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Product Selection
            </CardTitle>
            <CardDescription>
              Select product using cascading filters. Only products available in SFG Storage are shown.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Design Name - Searchable */}
            <div>
              <Label>Design Name *</Label>
              <Popover open={designSearchOpen} onOpenChange={setDesignSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={designSearchOpen}
                    disabled={loadingDesigns}
                    className="w-full justify-between font-normal"
                    data-testid="design-name-select"
                  >
                    {loadingDesigns ? "Loading..." : selectedDesignName || "Search design name..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Type to search..." />
                    <CommandList>
                      <CommandEmpty>No design found.</CommandEmpty>
                      <CommandGroup>
                        {designNames.map((name) => (
                          <CommandItem
                            key={name}
                            value={name}
                            onSelect={() => {
                              handleDesignNameChange(name);
                              setDesignSearchOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${selectedDesignName === name ? 'opacity-100' : 'opacity-0'}`} />
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {designNames.length === 0 && !loadingDesigns && (
                <p className="text-xs text-amber-600 mt-1">No products available in SFG Storage</p>
              )}
            </div>

            {/* Shade */}
            <div>
              <Label>Shade *</Label>
              <Select 
                value={selectedShade} 
                onValueChange={handleShadeChange}
                disabled={!selectedDesignName || loadingShades || shades.length === 0}
              >
                <SelectTrigger data-testid="shade-select">
                  <SelectValue placeholder={
                    loadingShades ? "Loading..." : 
                    !selectedDesignName ? "Select design first" : 
                    shades.length === 0 ? "No shades available" :
                    "Select shade"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {shades.map((shade) => (
                    <SelectItem key={shade} value={shade}>
                      {shade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Size */}
            <div>
              <Label>Size *</Label>
              <Select 
                value={selectedSize} 
                onValueChange={handleSizeChange}
                disabled={!selectedShade || loadingSizes || sizes.length === 0}
              >
                <SelectTrigger data-testid="size-select">
                  <SelectValue placeholder={
                    loadingSizes ? "Loading..." : 
                    !selectedShade ? "Select shade first" : 
                    sizes.length === 0 ? "No sizes available" :
                    "Select size"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Select 
                value={selectedDescription} 
                onValueChange={handleDescriptionChange}
                disabled={!selectedSize || loadingDescriptions || descriptions.length === 0}
              >
                <SelectTrigger data-testid="description-select">
                  <SelectValue placeholder={
                    loadingDescriptions ? "Loading..." : 
                    !selectedSize ? "Select size first" : 
                    descriptions.length === 0 ? "No descriptions available" :
                    "Select description"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {descriptions.map((desc) => (
                    <SelectItem key={desc} value={desc}>
                      {desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Item Barcode picker — surfaced whenever rates_master has
                at least one barcode for the chosen combo. Operator sees
                the customer-specific barcode + MRP before picking, even
                if only one option exists (2026-05-18 user fix). */}
            {selectedDescription && itemBarcodeOptions.length >= 1 && (
              <div data-testid="item-barcode-picker-block">
                <Label>
                  Item Barcode *{' '}
                  <span className="ml-2 text-xs font-normal text-amber-600">
                    {itemBarcodeOptions.length > 1
                      ? 'Multiple barcodes detected for this combo — pick one'
                      : 'Confirm the customer barcode + MRP for this slip'}
                  </span>
                </Label>
                <Select
                  value={selectedItemBarcode}
                  onValueChange={handleItemBarcodeChange}
                  disabled={loadingItemBarcodes}
                >
                  <SelectTrigger data-testid="item-barcode-select">
                    <SelectValue placeholder={
                      loadingItemBarcodes ? 'Loading…' : 'Select item barcode'
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {itemBarcodeOptions.map((opt) => {
                      const noStock = (opt.available_pieces || 0) <= 0;
                      return (
                        <SelectItem
                          key={opt.barcode}
                          value={opt.barcode}
                          data-testid={`item-barcode-option-${opt.barcode}`}
                          className={noStock ? 'opacity-60' : ''}
                        >
                          <span className="font-mono">{opt.barcode}</span>
                          {opt.mrp != null && (
                            <span className="ml-3 text-xs text-stone-500">
                              MRP ₹{opt.mrp}
                            </span>
                          )}
                          <span
                            className={
                              'ml-3 text-xs ' +
                              (noStock ? 'text-rose-700 font-semibold' : 'text-emerald-700')
                            }
                          >
                            · {noStock ? 'no stock yet' : `${opt.available_pieces} pcs avail.`}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Availability Info */}
        {loadingAvailability && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-4 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-blue-700">Checking availability...</span>
            </CardContent>
          </Card>
        )}

        {availability && !loadingAvailability && (
          <Card className={availability.available_pieces > 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
            <CardContent className="py-4">
              {availability.available_pieces > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Stock Available</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Item Barcode:</span>
                      <span className="ml-2 font-mono font-medium">{availability.item_barcode}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Available Bundles:</span>
                      <span className="ml-2 font-medium">{availability.available_bundles}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Available Pieces:</span>
                      <span className="ml-2 font-bold text-lg text-green-700">{availability.available_pieces}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-red-700">No stock available for this product</span>
                  </div>
                  {/* 2026-05-18 — When the SFG inventory shows raw STORED
                      pieces but they're all ghost-blocked, explain WHY
                      so the operator doesn't think the screen is wrong. */}
                  {(availability.raw_pieces || 0) > 0 && (
                    <div
                      className="text-xs text-red-700 bg-red-100 rounded-md px-3 py-2 mt-2"
                      data-testid="ghost-bundles-warning"
                    >
                      <div className="font-medium mb-1">
                        SFG shows {availability.raw_pieces} pcs in {availability.raw_bundle_count} bundle(s)
                        but {availability.excluded_bundle_count} bundle(s) are already consumed elsewhere:
                      </div>
                      <ul className="list-disc list-inside space-y-0.5 ml-1">
                        {availability.ghost_reasons?.in_completed_slip > 0 && (
                          <li>{availability.ghost_reasons.in_completed_slip} already picked in a COMPLETED slip</li>
                        )}
                        {availability.ghost_reasons?.in_fg > 0 && (
                          <li>{availability.ghost_reasons.in_fg} already in FG inventory</li>
                        )}
                        {availability.ghost_reasons?.in_active_slip > 0 && (
                          <li>{availability.ghost_reasons.in_active_slip} reserved by another active pick slip</li>
                        )}
                      </ul>
                      <div className="mt-1 italic">
                        Ask admin to run the SFG ghost-bundle heal to release these.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quantity Input */}
        {availability?.available_pieces > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Quantity</CardTitle>
              <CardDescription>
                Enter the TOTAL pieces to pick — the system auto-splits into pick slips of {MAX_PIECES_PER_SLIP} pcs each
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0)}
                  className="w-32 text-center text-lg font-bold"
                  data-testid="quantity-input"
                />
                <div className="text-sm text-gray-500">
                  / {availability.available_pieces} available
                </div>
              </div>
              {quantity > 0 && (
                <p className="text-sm text-gray-600 mt-2" data-testid="split-estimate">
                  Will generate{' '}
                  <span className="font-bold">
                    {Math.max(1, Math.ceil(Math.min(quantity, availability.available_pieces) / MAX_PIECES_PER_SLIP))}
                  </span>{' '}
                  pick slip(s) of up to {MAX_PIECES_PER_SLIP} pcs each.
                </p>
              )}
              {quantity > availability.available_pieces && (
                <p className="text-amber-600 text-sm mt-1" data-testid="shortfall-warning">
                  Only {availability.available_pieces} pcs in stock — slips will be created for the available
                  qty and {quantity - availability.available_pieces} pcs reported as short.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Button */}
        <Button 
          className="w-full h-14 text-lg"
          onClick={handleCreateSlip}
          disabled={!canCreate || creating}
          data-testid="create-slip-btn"
        >
          {creating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Creating Pick Slip...
            </>
          ) : (
            <>
              <Package className="w-5 h-5 mr-2" />
              Create Pick Slip
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SFGCreatePickSlip;
